import { useState, useEffect, useRef } from 'react'
import { Building2, ChevronDown, ArrowLeft, Loader2 } from 'lucide-react'
import { useTenant } from '@/hooks/useTenant'
import { listAllTenants, TenantSummary } from '@/services/api'

export function TenantSwitcher() {
  const { tenantId, isSwitched, switchTenant, resetTenant } = useTenant()
  const [open, setOpen] = useState(false)
  const [tenants, setTenants] = useState<TenantSummary[]>([])
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  async function handleOpen() {
    setOpen(!open)
    if (!open && tenants.length === 0) {
      setLoading(true)
      try {
        const data = await listAllTenants()
        setTenants(data.tenants || [])
      } catch (err) {
        console.error('Failed to load tenants:', err)
      } finally {
        setLoading(false)
      }
    }
  }

  const currentTenant = tenants.find(t => t.tenant_id === tenantId)
  const displayName = currentTenant?.name || tenantId || 'Unknown'

  return (
    <div className="relative" ref={ref}>
      {isSwitched ? (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium ring-1 ring-inset ring-purple-200">
            <Building2 size={12} />
            Viewing: {displayName}
          </div>
          <button
            onClick={resetTenant}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={12} />
            Back
          </button>
        </div>
      ) : (
        <button
          onClick={handleOpen}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Building2 size={13} />
          Switch Account
          <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}

      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 max-h-72 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={16} className="text-gray-300 animate-spin" />
            </div>
          ) : tenants.length === 0 ? (
            <div className="px-3 py-4 text-center">
              <p className="text-xs text-gray-400 mb-2">No companies yet</p>
              <a href="/admin" className="text-xs text-amber-600 hover:text-amber-700 font-medium">
                Go to Admin Panel to create one
              </a>
            </div>
          ) : (
            tenants.map(t => (
              <button
                key={t.tenant_id}
                onClick={() => {
                  switchTenant(t.tenant_id)
                  setOpen(false)
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                  t.tenant_id === tenantId ? 'bg-amber-50' : ''
                }`}
              >
                <div className="w-7 h-7 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                  <Building2 size={13} className="text-gray-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{t.name}</p>
                  <p className="text-[10px] text-gray-400 truncate">{t.tenant_id}</p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
